/**
 * Page Translation Scanner Hook
 * Proactively scans pages for translation keys and batch translates missing ones
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { translateBatch } from '../services/translationService';
import logger from '../utils/logger';
import { 
  setCachedTranslation, 
  invalidateOldTranslations,
  getCacheStats 
} from '../services/translationCacheManager';

/**
 * Get all translation keys that are commonly used in the app
 * These should match the keys actually used in components
 */
const getCommonTranslationKeys = (): string[] => {
  return [
    // Navigation keys (from Header component)
    'home',
    'newDebate', 
    'yourDebates',
    'publicDebates',
    'leaderboards',
    'cube',
    'rulesGuidelines',
    'about',
    'login',
    'register',
    'logout',
    'settings',
    
    // Action keys
    'create',
    'join',
    'view',
    'edit',
    'delete',
    'save',
    'cancel',
    'submit',
    'back',
    'next',
    'refresh',
    'search',
    
    // Status keys
    'active',
    'completed',
    'pending',
    'loading',
    
    // Page content keys (actually used in components)
    'activeDebates',
    'completedDebates',
    'publicDebates',
    'yourTurn', 
    'continueDebate',
    'viewResults',
    'createYourFirstDebate',
    'noActiveDebatesYet',
    'noCompletedDebatesYet',
    'searchDebates',
    'created',
    'noDescriptionProvided',
    'teams',
    'noActivityForOverSixDays',
    'viewOnly',
    'noActivePublicDebates',
    'noCompletedPublicDebates',
    
    // HomePage specific
    'welcomeToGauntletDebates',
    'platformForStructuredDebates',
    'jestersGauntletAlt',
    'searchYourDebates',
    'noPublicDebatesAvailable',
    'createNewDebateToBegin',
    'createNewDebate',
    'joinDebate',
    'viewDebate',
    
    // Email verification
    'emailNotVerified',
    'pleaseVerifyEmail',
    'verifyEmailNow',
    'checkEmailInbox',
    'resendVerificationEmail',
    'verificationEmailSent',
    'emailVerificationRequired',
    'mustVerifyEmailTo',
    
    // Forms and authentication
    'emailAddress',
    'password',
    'confirmPassword',
    'username',
    'signIn',
    'signUp',
    'dontHaveAccount',
    'alreadyHaveAccount',
    'forgotPassword',
    'rememberMe',
    
    // Invitations
    'inviteToTeam',
    'inviteToDebate',
    'sendInvitation',
    'invitationSent',
    'invitationExpired',
    'acceptInvitation',
    'declineInvitation',
    'youAreInvited',
    
    // Debate creation
    'debateTopic',
    'debateDescription',
    'selectTeamSize',
    'maxArguments',
    'timeLimit',
    'isPublic',
    'allowPublicJoining',
    
    // Rules page
    'rulesAndGuidelines',
    'conductGuidelines',
    'respectfulDebate',
    'evidenceBasedArguments',
    'timeManagement',
    
    // Common UI elements
    'minutes',
    'hours',
    'days',
    'yes',
    'no',
    'close',
    'continue',
    'finish',
    'step',
    'of',
    
    // Status messages
    'success',
    'error',
    'warning',
    'info',
    
    // Common phrases
    'welcome',
    'emailVerification',
    'required',
    'optional',
    
    // EmailVerificationBanner specific
    'limitedAccessVerifyEmail',
    'nobleUserLimitedAccess',
    'verificationEmailSentSuccess',
    'hideDetails',
    'showDetails',
    'sending',
    'resendEmail',
    'currentlyUnavailable',
    'availableNow',
    'tipCheckSpamFolder',
    'creatingNewDebates',
    'joiningDebatesViaInvites',
    'receivingEmailNotifications',
    'participatingInTeamDebates',
    'accessingPremiumFeatures',
    'viewingPublicDebates',
    'browsingTheCube',
    'viewingLeaderboards',
    'readingRulesAndAbout',
    
    // RegisterPage specific
    'registrationSuccessful',
    'accountCreatedSuccessfully',
    'verificationEmailSentTo',
    'checkInboxAndSpam',
    'canLoginNowLimited',
    'goToLogin',
    'passwordsDoNotMatch',
    'passwordTooShort',
    'mustAcceptTerms',
    'iAgreeToTerms',
    'termsOfService',
    'loginHere',
    
    // EmailVerificationGuard specific
    'needToVerifyEmailBefore',
    'sentVerificationLinkTo',
    'checkEmailAndClick',
    'whatYouCanDoUnverified',
    'viewPublicDebates',
    'browseLeaderboards',
    'exploreTheCube',
    'createOrParticipateDebates',
    'resendVerificationEmail',
    
    // DebateFormPage keys
    'enterTopic',
    'reviewGeneratedViews',
    'customize',
    'overview',
    'pleaseEnterDebateTopic',
    'failedToGenerateViews',
    'debateTopicPlaceholder',
    'debateDescriptionPlaceholder',
    'generateViews',
    'yourTeamName',
    'yourTeamViewpoint',
    'opponentTeamName',
    'customizePrompt',
    'customizeCounterPrompt',
    'teamConfiguration',
    'teamSize',
    'publicSpots',
    'allowPublicJoining',
    'createDebate',
    'createPrivateDebate',
    'createPublicDebate',
    'debateCreatedSuccessfully',
    'inviteOpponents',
    'shareInviteLink',
    'copyInviteLink',
    'inviteLinkCopied',
    
    // DebateInvitePage keys
    'theGauntletHasBeenThrown',
    'challengedYouToDebate',
    'debateOfHonor',
    'onTheTopic',
    'debateType',
    'publicDebateNotice',
    'privateDebateNotice',
    'description',
    'prepareYourDefense',
    'readCarefullyWarning',
    'yourOpponentsPosition',
    'yourPosition',
    'editAsNeeded',
    'yourBattleName',
    'battleNamePlaceholder',
    'youMustBeLoggedIn',
    'declineChallenge',
    'acceptTheChallenge',
    'returnToHome',
    'invalidOrExpiredInvitation',
    'pleaseLoginToViewInvitation',
    'invalidInvitationLink',
    'pleaseEnterTeamName',
    'successfullyJoinedDebate',
    'failedToJoinDebate',
    'pleaseLoginOrRegisterToJoin',
    
    // RulesPage keys
    'introductionToHonorableDebate',
    'productiveDebateRelies',
    'aiModeratorAnalyzes',
    'seriousIssuesPenalties',
    'issuesRequiringReformulation',
    'whenYourMessageContains',
    'debateMisconductTypes',
    'seriousIssuesCountAsPenalties',
    'theseAreOvertlyDisruptive',
    'countsAgainstYou',
    'requiresReformulation',
    'debateRulesSummary',
    'turnBasedStructure',
    'turnBasedDescription',
    'jestersGauntletPenalties',
    'jestersGauntletDescription',
    'flaggedMessages',
    'flaggedMessagesDescription',
    'reformulationProcess',
    'reformulationDescription',
    'conceding',
    'concedingDescription',
    'inactiveDebates',
    'inactiveDebatesDescription',
    
    // Misconduct type names and descriptions
    'adHominem',
    'adHominemDescription',
    'textualIntimidation',
    'textualIntimidationDescription',
    'grammarPolicing',
    'grammarPolicingDescription',
    'sarcasmOverload',
    'sarcasmOverloadDescription',
    'strawman',
    'strawmanDescription',
    'wordTwisting',
    'wordTwistingDescription',
    'whataboutism',
    'whataboutismDescription',
    'gishGallop',
    'gishGallopDescription',
    'shiftingGoalposts',
    'shiftingGoalpostsDescription',
    'quoteSniping',
    'quoteSnipingDescription',
    'sealioning',
    'sealioningDescription',
    'feigningConfusion',
    'feigningConfusionDescription',
    'overgeneralizations',
    'overgeneralizationsDescription',
    'falseDichotomy',
    'falseDichotomyDescription',
    'appealToAuthority',
    'appealToAuthorityDescription',
    'appealToEmotion',
    'appealToEmotionDescription',
    'cherryPicking',
    'cherryPickingDescription',
    'unsubstantiatedClaims',
    'unsubstantiatedClaimsDescription',
    'selectiveSkepticism',
    'selectiveSkepticismDescription',
    'circularReasoning',
    'circularReasoningDescription',
    'exampleOf',
    'betterApproach',
    'internalCode',
    
    // DebateRoomPage keys
    'messageFlags',
    'flagType',
    'options',
    'reformulateYourMessage',
    'concedeDefeat',
    'concedeDebate',
    'backHome',
    'areYouSureYouWantToConcede',
    'debateResults',
    'debateCompleted',
    'thisDebateHasConcluded',
    'theWinnerIs',
    'summaryGenerated',
    'viewSummary',
    'jestersGauntletDetected',
    'reformulationNeeded',
    'thisMessageContainsLanguage',
    
    // Additional DebateFormPage keys
    'swapViews',
    
    // LeaderboardPage keys
    'leaderboard.leaderboards',
    'leaderboard.viewTopPerformers',
    'leaderboard.topDebaters',
    'leaderboard.topDebates',
    'leaderboard.rankingBy',
    'leaderboard.rank',
    'leaderboard.user',
    'leaderboard.publicDebates',
    'leaderboard.wins',
    'leaderboard.winRate',
    'leaderboard.excaliburIndex',
    'leaderboard.explanationStrength',
    'leaderboard.argumentClarity',
    'leaderboard.rebuttalEffectiveness',
    'leaderboard.evidenceQuality',
    'leaderboard.steelmanningRating',
    'leaderboard.topic',
    'leaderboard.teams',
    'leaderboard.overallQuality',
    'leaderboard.explanationQuality',
    'leaderboard.logicalCoherence',
    'leaderboard.civilDiscourse',
    'leaderboard.messageCount',
    'leaderboard.date',
    'leaderboard.noUsersFound',
    'leaderboard.noDebatesFound',
    
    // AboutPage keys
    'aboutPage.title',
    'aboutPage.subtitle',
    'aboutPage.mission.title',
    'aboutPage.mission.description',
    'aboutPage.mission.goal',
    'aboutPage.keyFeatures.title',
    'aboutPage.keyFeatures.structuredDebate',
    'aboutPage.keyFeatures.structuredDebateDescription',
    'aboutPage.keyFeatures.jesterGauntlet',
    'aboutPage.keyFeatures.jesterGauntletDescription',
    'aboutPage.keyFeatures.honorBasedScoring',
    'aboutPage.keyFeatures.honorBasedScoringDescription',
    'aboutPage.keyFeatures.fairResolutions',
    'aboutPage.keyFeatures.fairResolutionsDescription',
    'aboutPage.keyFeatures.argumentRepository',
    'aboutPage.keyFeatures.argumentRepositoryDescription',
    'aboutPage.honorableChallenges.title',
    'aboutPage.honorableChallenges.description',
    'aboutPage.honorableChallenges.howToChallenge',
    'aboutPage.honorableChallenges.jesterGauntlet',
    'aboutPage.howItWorks.title',
    'aboutPage.howItWorks.step1',
    'aboutPage.howItWorks.step2',
    'aboutPage.howItWorks.step3',
    'aboutPage.howItWorks.step4',
    'aboutPage.howItWorks.step5',
    'aboutPage.excaliburIndex.title',
    'aboutPage.excaliburIndex.description',
    'aboutPage.excaliburIndex.calculation',
    'aboutPage.excaliburIndex.qualityComponent',
    'aboutPage.excaliburIndex.qualityComponentDescription',
    'aboutPage.excaliburIndex.performanceComponent',
    'aboutPage.excaliburIndex.performanceComponentDescription',
    'aboutPage.excaliburIndex.goodFaithComponent',
    'aboutPage.excaliburIndex.goodFaithComponentDescription',
    'aboutPage.excaliburIndex.scale',
    'aboutPage.excaliburIndex.technicalImplementation',
    'aboutPage.excaliburIndex.publicPrivateIndices',
    'aboutPage.evolvingSystem.title',
    'aboutPage.evolvingSystem.description',
    'aboutPage.evolvingSystem.communityDrivenImprovement',
    'aboutPage.evolvingSystem.communityDrivenImprovementDescription',
    'aboutPage.evolvingSystem.learningCurve',
    'aboutPage.evolvingSystem.learningCurveDescription',
    'aboutPage.evolvingSystem.falsePositives',
    'aboutPage.evolvingSystem.falsePositivesDescription',
    'aboutPage.evolvingSystem.nature',
    'aboutPage.evolvingSystem.reformulation',
    'aboutPage.callToAction.title',
    'aboutPage.callToAction.description',
    'aboutPage.callToAction.startNewDebate',
    'aboutPage.callToAction.checkPublicDebates',
    'aboutPage.callToAction.imageAlt',
  ];
};

/**
 * Get all translation keys from i18n resources for a given language
 */
const getDefinedKeys = (i18n: any, language: string, namespace: string = 'common'): Set<string> => {
  const store = i18n.store as any;
  
  const keys = new Set<string>();
  
  if (store.data[language] && store.data[language][namespace]) {
    Object.keys(store.data[language][namespace]).forEach(key => {
      keys.add(key);
    });
  }
  
  return keys;
};

/**
 * Hook that scans for missing translation keys and batch translates them
 * DISABLED: Now using comprehensive static translations
 */
export const usePageTranslationScanner = (enabled: boolean = true) => {
  const { i18n, t } = useTranslation();

  useEffect(() => {
    // DISABLED - we now use comprehensive static translations
    logger.info(`🚫 Page translation scanner disabled - using static translations only`);
    return;
    
    // Only scan if explicitly enabled and not English (unreachable when scanner disabled)
    // eslint-disable-next-line no-unreachable
    if (!enabled || i18n.language === 'en') {
      return;
    }

    // Add a delay to prevent scanning on every component mount
    // and only scan once per language change per session
    const sessionKey = `translation_scan_${i18n.language}_${window.location.pathname}`;
    const hasScannedThisSession = sessionStorage.getItem(sessionKey);
    
    if (hasScannedThisSession) {
      logger.info(`🔍 Translation scan skipped for ${i18n.language} on ${window.location.pathname} - already scanned this session`);
      return;
    }

    const scanAndTranslate = async () => {
      try {
        logger.info(`🔍 Scanning for missing translation keys (${i18n.language}) on page: ${window.location.pathname}`);
        
        // Get all common translation keys
        const allKeys = getCommonTranslationKeys();
        logger.info(`📄 Checking ${allKeys.length} common translation keys`);
        
        // Get already defined keys for current language
        const definedKeys = getDefinedKeys(i18n, i18n.language);
        logger.info(`📚 Already have ${definedKeys.size} translations for ${i18n.language}`);
        
        // Find missing translation keys
        const missingKeys: string[] = [];
        const missingTexts: string[] = [];
        
        allKeys.forEach(key => {
          if (!definedKeys.has(key)) {
            // Key is missing, get the English text for it
            const englishText = t(`common:${key}`, { lng: 'en', defaultValue: null });
            if (englishText && englishText !== `common:${key}`) {
              missingKeys.push(key);
              missingTexts.push(englishText);
            } else {
              // If no English translation exists, log a warning and skip this key
              logger.warn(`⚠️ No English translation found for key: "${key}"`);
            }
          }
        });

        if (missingKeys.length > 0) {
          logger.info(`🚨 Found ${missingKeys.length} missing translation keys:`, missingKeys);
          logger.info(`📝 Texts to translate:`, missingTexts);
          
          // Clean up outdated translations before adding new ones
          invalidateOldTranslations(missingTexts, i18n.language);
          
          // Batch translate missing texts
          logger.info(`🚀 Batch translating ${missingTexts.length} missing texts to ${i18n.language}`);
          const translations = await translateBatch(missingTexts, i18n.language);
          
          // Add translations to i18n store
          const store = i18n.store as any;
          if (!store.data[i18n.language]) {
            store.data[i18n.language] = {};
          }
          if (!store.data[i18n.language]['common']) {
            store.data[i18n.language]['common'] = {};
          }

          let addedCount = 0;
          missingKeys.forEach((key, index) => {
            const originalText = missingTexts[index];
            const translation = translations[originalText];
            
            if (translation && translation !== originalText) {
              // Use smart cache manager for storage
              setCachedTranslation(originalText, i18n.language, translation);
              
              // Add to i18n store with the correct key
              store.data[i18n.language]['common'][key] = translation;
              logger.info(`✅ Added translation: "${key}" -> "${translation}" (from "${originalText}")`);
              addedCount++;
            }
          });

          if (addedCount > 0) {
            // Show cache statistics
            const stats = getCacheStats();
            logger.info(`📊 Cache stats: ${stats.totalEntries} total entries, ${Math.round(stats.totalSize / 1024)}KB`);
            
            logger.info(`🔄 Triggering UI re-render for ${addedCount} new translations`);
            i18n.emit('added', i18n.language, 'common');
            i18n.emit('languageChanged', i18n.language);
            
            // Force re-render
            setTimeout(() => {
              i18n.changeLanguage(i18n.language);
            }, 100);
          }
          
          // Mark this page/language combination as scanned
          sessionStorage.setItem(sessionKey, Date.now().toString());
          
        } else {
          logger.info(`✅ Translation scan complete - no missing keys found for ${i18n.language}`);
          // Still mark as scanned to prevent re-scanning
          sessionStorage.setItem(sessionKey, Date.now().toString());
        }

      } catch (error) {
        logger.error('❌ Translation scan failed:', error);
      }
    };

    // Scan after a longer delay to ensure page is fully loaded and prevent excessive calls (unreachable when scanner disabled)
    // eslint-disable-next-line no-unreachable
    const timer = setTimeout(scanAndTranslate, 3000); // Increased from 1000ms to 3000ms
    
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n not needed when scanner disabled
  }, [i18n.language, enabled, t]);
};

export default usePageTranslationScanner; 